import { ColDef } from 'ag-grid-community'
import { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { CustomCellRendererProps } from 'ag-grid-react'
import { MAIL_LIST_COLUMN_STATE_KEY } from '@/utils/constants.ts'
import { marketDateTimezoneFormatter } from '@/utils/formatters.ts'
import { EditOutlined } from '@ant-design/icons'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button.tsx'
import { useTranslation } from 'react-i18next'
import { useIsSuperViewer } from '@/common/hooks/use-is-superviewer.ts'
import { InfiniteGrid } from '@/common/ag-grid/infinite-grid.tsx'
import {
  GridRef,
  InfiniteGridFetchParams,
} from '@/common/ag-grid/grid.types.ts'
import { useLazyListMailEntriesQuery } from '@/apis/mail-list/mail-list-api.api.ts'
import { MailListEntry } from '@/apis/mail-list/mail-list-api.def.ts'
import { MailListType } from '@/enums/mail-list.enum.ts'
import { splitCategory } from '@/utils/selector-options.ts'
import { MailListFilters } from './mail-list-controls.tsx'

// Maps ag-grid colId to the backend orderBy field name.
const mailListSortMapping: Record<string, string> = {
  email: 'Email',
  fullName: 'FullName',
  firmName: 'FirmName',
  firmRole: 'FirmRole',
  category: 'Category',
  groupRole: 'GroupRole',
  updatedAt: 'UpdatedAt',
}

interface MailListTableProps {
  listType: MailListType
  filters: MailListFilters
  onEditEntry: (email: string) => void
  onRemoveEntry: (email: string) => void
  height: number
  ref?: React.Ref<GridRef>
}

export const MailListTable: React.FC<MailListTableProps> = ({
  listType,
  filters,
  onEditEntry,
  onRemoveEntry,
  height,
  ref,
}) => {
  const { t } = useTranslation()
  const isSuperViewer = useIsSuperViewer()
  const gridRef = useRef<GridRef>(null)
  useImperativeHandle(
    ref,
    () => ({
      refresh: () => gridRef.current?.refresh(),
      reload: () => gridRef.current?.reload(),
      getGridApi: () => gridRef.current?.getGridApi() ?? null,
    }),
    []
  )
  const [triggerListMailEntries] = useLazyListMailEntriesQuery()

  const columnDefs = useMemo<ColDef[]>(() => {
    const baseColumns: ColDef[] = [
      ...(!isSuperViewer
        ? [
            {
              headerName: '',
              field: 'id',
              width: 80,
              minWidth: 80,
              maxWidth: 80,
              sortable: false,
              suppressHeaderMenuButton: true,
              cellRenderer: (props: CustomCellRendererProps) => (
                <div className={'mail-list-table__action-cell'}>
                  <IdeyaButton
                    ideyaType={'bw'}
                    icon={<EditOutlined />}
                    onClick={() => onEditEntry(props.data.email)}
                  />
                </div>
              ),
            },
          ]
        : []),
      {
        headerName: t('mail_list.table.columns.email'),
        field: 'email',
      },
      {
        headerName: t('mail_list.table.columns.full_name'),
        field: 'fullName',
      },
      {
        headerName: t('mail_list.table.columns.firm'),
        field: 'firmName',
      },
      {
        headerName: t('mail_list.table.columns.firm_role'),
        field: 'firmRole',
      },
      {
        headerName: t('mail_list.table.columns.category'),
        field: 'category',
      },
      {
        headerName: t('mail_list.table.columns.group_role'),
        field: 'groupRole',
      },
      {
        headerName: t('mail_list.table.columns.updated_at'),
        field: 'updatedAt',
        valueFormatter: (params) =>
          marketDateTimezoneFormatter(
            {
              ...params,
              data: {
                ...params.data,
                marketTimeZone:
                  // Browser timezone
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              },
            },
            { showTimezone: true }
          ),
      },
    ]

    return baseColumns
  }, [t, isSuperViewer, onEditEntry])

  // Filters and listType are external controls; read the latest values inside
  // fetchData without recreating the datasource on every change.
  const filtersRef = useRef({ listType, filters })
  filtersRef.current = { listType, filters }

  const fetchData = useCallback(
    async (params: InfiniteGridFetchParams) => {
      const { listType: currentListType, filters: currentFilters } =
        filtersRef.current
      // InfiniteGrid maps colId to the backend field via sortMapping before
      // calling fetchData, so colId is already the orderBy value.
      const sortField = params.sortModel[0]?.colId
      const { region, netCategory } = splitCategory(currentFilters.category)
      const result = await triggerListMailEntries({
        page: Math.floor(params.startRow / params.pageSize) + 1,
        pageSize: params.pageSize,
        orderBy: sortField || undefined,
        orderDirection: params.sortModel[0]?.sort?.toUpperCase(),
        listType: currentListType,
        firmId: currentFilters.firmId || undefined,
        firmRole: currentFilters.firmRole || undefined,
        region: region || undefined,
        netCategory: netCategory || undefined,
        groupRole: currentFilters.groupRole || undefined,
        name: currentFilters.searchName || undefined,
      }).unwrap()
      return { rows: result.data.entries, totalCount: result.data.total }
    },
    [triggerListMailEntries]
  )

  // Reload when external filters or the selected list change so the grid always
  // re-fetches with the current query. Serialized value drives the effect so
  // re-selecting the same value still reloads deterministically.
  const queryKey = JSON.stringify([listType, filters])
  const isFirstQueryRunRef = useRef(true)
  useEffect(() => {
    if (isFirstQueryRunRef.current) {
      isFirstQueryRunRef.current = false
      return
    }
    gridRef.current?.reload()
  }, [queryKey])

  // Right-click menu: remove the entry from the list currently being viewed
  // (Block view removes from Block, White view removes from White).
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): MenuItemDef[] => {
      const entry = params.node?.data as MailListEntry | undefined
      if (isSuperViewer || !entry) return []
      const removeLabel =
        listType === MailListType.White
          ? t('mail_list.table.remove_from_white_list')
          : t('mail_list.table.remove_from_block_list')
      return [
        {
          name: removeLabel,
          action: () => onRemoveEntry(entry.email),
        },
      ]
    },
    [isSuperViewer, listType, t, onRemoveEntry]
  )

  return (
    <InfiniteGrid
      ref={gridRef}
      columnDefs={columnDefs}
      storageKey={MAIL_LIST_COLUMN_STATE_KEY}
      fetchData={fetchData}
      sortMapping={mailListSortMapping}
      getContextMenuItems={getContextMenuItems}
      canExport={false}
      height={height}
    />
  )
}
